import React, { useEffect, useRef, useState } from 'react';
import { Paper, Typography, makeStyles, Tooltip, IconButton, CircularProgress } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';

const useStyles = makeStyles((theme) => ({
    container: {
        position: 'fixed',
        bottom: 10,
        right: 10,
        width: 320,
        zIndex: 1301,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        background: '#fff',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        border: '1px solid #ddd',
        borderBottom: 'none',
    },
    btn: {
        cursor: 'pointer'
    },
    listBox: {
        maxHeight: 260,
        overflowY: 'auto',
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        border: '1px solid #ddd',
        background: '#fff',
        transition: 'height 0.5s ease-in-out',
        '&::-webkit-scrollbar': {
            width: '0.5em',
            height: '0.5em',
        },
        '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
            webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ccc',
            borderRadius: '5px',
        }
    },
    itemPaper: {
        padding: 12,
        borderBottom: '1px solid #eee',
    }
}));

const AiGenerationNotifications = ({ items, onClose, messageList = [], generationMode = "" }) => {
    const classes = useStyles();
    const cancelFn = useRef();
    const [minimized, setMinimized] = useState(false);
    const [progressMessage, setProgressMessage] = useState([]);

    const completedCount = items.filter(i => i.status === 'completed').length;
    const errorCount = items.filter(i => i.status === 'error').length;
    const generatingCount = items.filter(i => i.status === 'generating').length;
    const totalCount = items.length;
    const allDone = generatingCount === 0;

    const iconStyle = { width: 22, height: 22 };

    const handleProgressMessages = (delay = 1000, messages = []) => {
        if (!messages.length) return;
    
        let index = 0;
    
        setProgressMessage(messages[0]);
    
        const interval = setInterval(() => {
            index++;
    
            if (index >= messages.length) {
                clearInterval(interval);
                setProgressMessage(messages[messages.length - 1]); 
                return;
            }
    
            setProgressMessage(messages[index]);
        }, delay);
    
        return () => clearInterval(interval);
    };


    useEffect(() => {
        if (!messageList?.length) return;
    
        if (typeof cancelFn.current === "function") {
            cancelFn.current();
        }
    
        cancelFn.current = handleProgressMessages(2000, messageList);
    
        return () => {
            if (typeof cancelFn.current === "function") {
                cancelFn.current();
            }
        };
    }, [messageList]); 

    return (
        <div className={classes.container}>

            <div className={classes.header}>
                <Typography variant="body2">
                    {
                        completedCount + errorCount === totalCount ? "Generation Completed" : generationMode == "parallel" ? `Generating ${totalCount} data transactions` : `Generating ${completedCount + errorCount + 1}/${totalCount} items`
                    }
                    
                </Typography>
                <div>
                    <IconButton size="small" onClick={() => setMinimized(!minimized)}>
                        {minimized ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>

                    {allDone && (
                        <IconButton size="small" title={"Close"} onClick={(e) => {
                            e.stopPropagation();
                            onClose && onClose();
                        }}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
            </div>

            {!minimized && (
                <div className={classes.listBox}>
                    {items.map((gen) => (
                        <Paper key={gen.id} className={classes.itemPaper} elevation={0}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div style={{ flex: 1, width: '80%' }}>
                                    <Tooltip title={gen.name} arrow disablePortal>
                                        <Typography noWrap>
                                            {gen.name}
                                        </Typography>
                                    </Tooltip>
                                    {
                                        (gen.status === "generating" && progressMessage) &&
                                        <Typography str style={{fontSize: '0.7rem', display: 'flex', alignItems: 'flex-end'}}>
                                           {progressMessage.length > 45
                                            ? `${progressMessage.substring(0, 45)}`
                                            : progressMessage
                                            }
                                            <span style={{paddingLeft: 2}} class="dot-loader" aria-label="Loading"></span>
                                        </Typography>
                                    }
                                </div>

                                {/* Status Icon */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 5 }}>
                                    {gen.status === 'pending' && (
                                        <Tooltip title="Pending" arrow>
                                            <HourglassEmptyIcon style={iconStyle} />
                                        </Tooltip>
                                    )}
                                    {gen.status === 'generating' && (
                                        <Tooltip title="In Progress" arrow>
                                            <CircularProgress size={16} thickness={5} />
                                        </Tooltip>
                                    )}
                                    {gen.status === 'completed' && (
                                        <Tooltip title="Completed" arrow>
                                            <CheckCircleOutlineIcon style={{ ...iconStyle, color: 'green' }} />
                                        </Tooltip>
                                    )}
                                    {gen.status === 'error' && (
                                        <Tooltip title="Failed" arrow>
                                            <ErrorOutlineIcon style={{ ...iconStyle, color: 'red' }} />
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        </Paper>
                    ))}
                </div>
            )
            }
        </div >
    );
};

export default AiGenerationNotifications;
